import React, { Component } from "react";
import ReactTooltip from "react-tooltip";
import moment from "moment";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css"; // Core styles
import "ag-grid-community/styles/ag-theme-balham.css"; // Optional theme
import "./ag-theme-ela.css";

const DEFAULT_MIN_WIDTH_CELL = 10;
const DEFAULT_MAX_WIDTH_CELL = 800;

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  minWidth: DEFAULT_MIN_WIDTH_CELL,
  maxWidth: DEFAULT_MAX_WIDTH_CELL,
  cellStyle: {
    borderRight: "1px solid #ccc", // Adds a light gray divider
  },
};

const columns = [
  {
    headerName: "Job#",
    field: "jobNum",
    width: 100,
  },
  {
    headerName: "Date",
    field: "jobDateTime",
    width: 120,
    cellRenderer: "dateCellRenderer",
  },
  {
    headerName: "Time",
    field: "jobDateTime",
    width: 150,
    cellRenderer: "timeCellRenderer",
  },
  {
    headerName: "Claimant",
    field: "clmLastName",
    width: 150,
    cellRenderer: "claimantCellRenderer",
  },
  {
    headerName: "Provider",
    field: "providerName",
    width: 150,
    cellRenderer: "providerCellRenderer",
  },
  { headerName: "Chk#", field: "intCheckNum", width: 100 },
  {
    headerName: "Chk Date",
    field: "intCheckDate",
    width: 120,
    cellRenderer: "checkDateCellRenderer",
  },
  {
    headerName: "Fee",
    field: "intFee",
    width: 100,
    cellRenderer: "feeCellRenderer",
  },
  {
    headerName: "OT",
    field: "intOTFee",
    width: 90,
    cellRenderer: "otFeeCellRenderer",
  },
  {
    headerName: "OTH",
    field: "intFeeOther",
    width: 90,
    cellRenderer: "othFeeCellRenderer",
  },
  {
    headerName: "Total",
    field: "jobTotal",
    width: 100,
    cellRenderer: "totalCellRenderer",
  },
  {
    headerName: "Address Info",
    field: "aptStreet",
    width: 200,
    cellRenderer: "addressCellRenderer",
  },
  {
    headerName: "Attachment",
    field: "attachmentFileLocationS3",
    width: 120,
    sortable: false,
    filter: false,
    cellRenderer: "attachmentCellRenderer",
  },
];

export class WorkOrderIntTableHistory extends Component {
  static displayName = WorkOrderIntTableHistory.name;

  constructor(props) {
    super(props);
    this.state = {
      columnWidths: columns.reduce((acc, col) => {
        acc[col.field] = col.width || DEFAULT_MIN_WIDTH_CELL; // Initialize widths
        return acc;
      }, {}),
      isResizing: -1,
      clickCount: 0,
      selectedIndex: this.props.selectedIndex || -1,
      searchModel: this.props.searchModel || {
        SortBy: "jobDateTime",
        SortDirection: "desc",
      },
    };

    // Bind methods
    this.onColumnResized = this.onColumnResized.bind(this);
    this.autoSizeUnspecifiedColumns =
      this.autoSizeUnspecifiedColumns.bind(this);
    this.adjustHeaderHeight = this.adjustHeaderHeight.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.onDataLoaded = this.onDataLoaded.bind(this);
    this.requestSort = this.requestSort.bind(this);
    this.setData = this.setData.bind(this);
    this.handleEndTimeClick = this.handleEndTimeClick.bind(this);
  }

  componentDidMount() {
    ReactTooltip.rebuild();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedIndex !== this.props.selectedIndex) {
      this.setState({ selectedIndex: this.props.selectedIndex });
    }
    if (prevProps.searchModel !== this.props.searchModel) {
      this.setState({ searchModel: this.props.searchModel });
    }
  }

  // Method to handle sorting - implementing requestSort from original code
  requestSort = (field) => () => {
    const newSearchModel = { ...this.state.searchModel };
    if (newSearchModel.SortBy === field) {
      newSearchModel.SortDirection =
        newSearchModel.SortDirection === "asc" ? "desc" : "asc";
    } else {
      newSearchModel.SortBy = field;
      newSearchModel.SortDirection = "asc";
    }

    this.setState({ searchModel: newSearchModel });

    if (this.props.onSortChanged) {
      this.props.onSortChanged(newSearchModel);
    }
  };

  // Method to handle Final Conf button clicks
  setData(workorder, index, isDoubleClick) {
    this.setState({ selectedIndex: index });
    if (this.props.onFinalConfClick) {
      this.props.onFinalConfClick(workorder, index, isDoubleClick);
    }
  }

  // Method to handle End Time button clicks
  handleEndTimeClick(workorder, index, isDoubleClick) {
    this.setState({ selectedIndex: index });
    if (this.props.onEndTimeClick) {
      this.props.onEndTimeClick(workorder, index, isDoubleClick);
    }
  }

  autoSizeUnspecifiedColumns = () => {
    const allColumnIds = [];
    this.gridColumnApi.getColumns().forEach((column) => {
      // Check if the width is not set in the column definition
      if (column.getColDef().width === undefined) {
        allColumnIds.push(column.colId);
      }
    });
    if (allColumnIds.length > 0) {
      this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    }
  };

  adjustHeaderHeight = (gridApi) => {
    window.requestAnimationFrame(() => {
      const allColumnHeaders = document.querySelectorAll(
        ".ag-header-cell-text"
      );
      let maxHeight = 0;
      allColumnHeaders.forEach((header) => {
        if (header.parentElement.clientHeight > maxHeight) {
          maxHeight = header.parentElement.clientHeight;
        }
      });

      maxHeight += 10;
      gridApi.setHeaderHeight(maxHeight);
    });
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.setState({ gridApiReady: true });
    this.adjustHeaderHeight(params.api);
    this.autoSizeUnspecifiedColumns(false);
  };

  onDataLoaded = (newData) => {
    this.gridApi.setRowData(newData);
    this.autoSizeUnspecifiedColumns(true);
  };

  onColumnResized = (params) => {
    if (params.finished) {
      // Update the state with new column width
      const newWidths = { ...this.state.columnWidths };
      params.columns.forEach((column) => {
        newWidths[column.getColId()] = column.getActualWidth();
      });
      this.setState({ columnWidths: newWidths }, () => {
        this.adjustHeaderHeight(this.gridApi);
      });
    }
  };

  render() {
    // Update column definitions to include current sort state
    const updatedColumns = columns.map((col) => {
      if (col.field === this.state.searchModel.SortBy) {
        return {
          ...col,
          sort: this.state.searchModel.SortDirection.toLowerCase(),
        };
      }
      return col;
    });

    const gridOptions = {
      suppressSizeToFit: false,
      columnDefs: updatedColumns,
      rowData: this.props.workOrders,
      context: {
        componentParent: this,
        gridApi: this.gridApi,
        state: this.state,
      },
      animateRows: true,
      defaultColDef: defaultColDef,
      onColumnResized: this.onColumnResized,
      components: {
        dateCellRenderer: this.dateCellRenderer.bind(this),
        timeCellRenderer: this.timeCellRenderer.bind(this),
        claimantCellRenderer: this.claimantCellRenderer.bind(this),
        providerCellRenderer: this.providerCellRenderer.bind(this),
        checkDateCellRenderer: this.checkDateCellRenderer.bind(this),
        feeCellRenderer: this.feeCellRenderer.bind(this),
        otFeeCellRenderer: this.otFeeCellRenderer.bind(this),
        othFeeCellRenderer: this.othFeeCellRenderer.bind(this),
        totalCellRenderer: this.totalCellRenderer.bind(this),
        addressCellRenderer: this.addressCellRenderer.bind(this),
        attachmentCellRenderer: this.attachmentCellRenderer.bind(this),
      },
      // rowHeight: 45, // Adjust as needed for your data
      tooltipShowDelay: 0,
      enableBrowserTooltips: true,
      getRowId: (params) => params.data.jobId,
      getRowClass: (params) =>
        params.rowIndex === this.state.selectedIndex ? "selected-row" : "",
      onSortChanged: () => {
        if (this.gridColumnApi) {
          const sortedCols = this.gridColumnApi
            .getColumnState()
            .filter((col) => col.sort);
          if (sortedCols.length > 0) {
            const newSearchModel = {
              SortBy: sortedCols[0].colId,
              SortDirection: sortedCols[0].sort,
            };
            this.setState({ searchModel: newSearchModel });
            if (this.props.onSortChanged) {
              this.props.onSortChanged(newSearchModel);
            }
          }
        }
      },
    };

    return (
      <div
        className="ag-theme-balham"
        style={{
          width: "100%",
          height: "600px",
        }}
      >
        <AgGridReact {...gridOptions} onGridReady={this.onGridReady} />
      </div>
    );
  }

  // Cell renderer for Date column
  dateCellRenderer(params) {
    if (!params.data || !params.value) return "";
    return moment(params.value).format("MM/DD/YYYY");
  }

  // Cell renderer for Time column
  timeCellRenderer(params) {
    if (!params.data || !params.value) return "";
    const startTime = moment(params.value).format("h:mm A");
    const finishTime = params.data.finishTime
      ? ` to ${moment(params.data.finishTime).format("h:mm A")}`
      : "";
    return `${startTime}${finishTime}`;
  }

  // Cell renderer for Claimant column
  claimantCellRenderer(params) {
    if (!params.data) return "";
    const fullName = `${params.data.clmFirstName} ${params.data.clmLastName}`;
    return params.data.hasClaimantDupWO ? (
      <span style={{ color: "red" }}>{fullName}</span>
    ) : (
      fullName
    );
  }

  // Cell renderer for Provider column
  providerCellRenderer(params) {
    if (!params.data) return '';
    return params.data.providerName || '';
  }

  // Cell renderer for Check Date column
  checkDateCellRenderer(params) {
    if (!params.data || !params.value) return "";
    return moment(params.value).format("MM/DD/YYYY");
  }

  // Cell renderer for Fee column
  feeCellRenderer(params) {
    if (!params.data || params.value === null) return "";
    return `$${params.value}`;
  }

  // Cell renderer for OT Fee column
  otFeeCellRenderer(params) {
    if (!params.data || params.value === null) return "";
    return `$${params.value}`;
  }

  // Cell renderer for Other Fee column
  othFeeCellRenderer(params) {
    if (!params.data || params.value === null) return "";
    return `$${params.value}`;
  }

  // Cell renderer for Total column
  totalCellRenderer(params) {
    if (!params.data || params.data.intFee === null) return "";
    const total =
      (params.data.intFee || 0) +
      (params.data.intOTFee || 0) +
      (params.data.intFeeOther || 0);
    return `$${total}`;
  }

  // Cell renderer for Address Info column
  addressCellRenderer(params) {
    if (!params.data) return "";
    return (
      <div>
        {params.data.aptStreet} {params.data.aptCity} {params.data.aptState}{" "}
        {params.data.aptZip}
      </div>
    );
  }

  // Cell renderer for Interpreter Attachment download link
  attachmentCellRenderer(params) {
    if (!params.data || !params.data.attachmentFileLocationS3) return null;
    return (
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          this.props.onDownloadAttachment(
            params.data.attachmentJobHistoryId,
            params.data.attachmentFileName
          );
        }}
      >
        Download
      </a>
    );
  }
}
